import axios from 'axios';
import { io as socketIOClient } from 'socket.io-client';
import { getServerUrl } from '../config/runtimeConfig';

const API_BASE_URL = getServerUrl();

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/images`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 2 minutes for image uploads
});

// Request interceptor for auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const imageService = {
  /**
   * Delete all images in a session (blobs + DB) without deleting the session itself
   * @param {string} sessionId - Session unique ID
   * @returns {Promise} Delete operation results
   */
  async deleteAllImagesInSession(sessionId) {
    try {
      console.log('📡 Deleting all images in session:', sessionId);
      const response = await apiClient.delete(`/session/${sessionId}`);
      console.log('✅ All images deleted from session:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to delete all images in session:', error);
      throw error;
    }
  },
  // ...existing imageService methods...
};


// Request interceptor for auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access - only redirect if we don't have a token
      // or if this is not already the auth page
      const hasToken = localStorage.getItem('token');
      const isAuthPage = window.location.pathname === '/auth';
      if (hasToken) {
        // Token exists but is invalid, remove it
        localStorage.removeItem('token');
      }
      // Only redirect if we're not already on the auth page
      if (!isAuthPage) {
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/auth?from=${encodeURIComponent(currentPath)}`;
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Image Service API
 * Handles all image-related API operations including upload, CRUD, and metadata management
 */
class ImageService {

  static MAX_FILES_PER_UPLOAD_REQUEST = 10;

  buildUploadFormData(files, metadata = {}) {
    const formData = new FormData();

    formData.append('sessionId', metadata.sessionId);

    Array.from(files).forEach((file, index) => {
      formData.append('images', file);

      if (metadata.names && metadata.names[index]) {
        formData.append(`name_${index}`, metadata.names[index]);
      }
      if (metadata.descriptions && metadata.descriptions[index]) {
        formData.append(`description_${index}`, metadata.descriptions[index]);
      }
      if (metadata.tags && metadata.tags[index]) {
        formData.append(`tags_${index}`, metadata.tags[index]);
      }
    });

    return formData;
  }

  /**
   * Delete all images in a session (blobs + DB) without deleting the session itself
   * @param {string} sessionId - Session unique ID
   */
  async deleteAllImagesInSession(sessionId) {
    try {
      console.log('📡 Deleting all images in session:', sessionId);
      const response = await apiClient.delete(`/session/${sessionId}`);
      console.log('✅ All images deleted from session:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to delete all images in session:', error);
      throw error;
    }
  }

  /**
   * Get all images with filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise} API response with images and pagination
   */
  async getImages(params = {}) {
    try {
      console.log('📡 Fetching images with params:', params);
      
      const response = await apiClient.get('/', { params });
      
      console.log('✅ Images fetched successfully:', {
        count: response.data.images?.length || 0,
        total: response.data.pagination?.totalCount || 0
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch images:', error);
      throw this.handleError(error, 'Failed to fetch images');
    }
  }

  /**
   * Get specific image by ID
   * @param {string} imageId - Image unique ID
   * @returns {Promise} Image data
   */
  async getImage(imageId) {
    try {
      console.log('📡 Fetching image:', imageId);
      
      const response = await apiClient.get(`/${imageId}`);
      
      console.log('✅ Image fetched successfully:', {
        id: response.data.uniqueId,
        name: response.data.name
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch image:', error);
      throw this.handleError(error, 'Failed to fetch image');
    }
  }

  /**
   * Upload multiple images with metadata
   * @param {FileList|Array} files - Image files to upload
   * @param {Object} metadata - Additional metadata for files (including sessionId)
   * @param {Function} onProgress - Progress callback
   * @returns {Promise} Upload result
   */
  async uploadImages(files, metadata = {}, onProgress = null) {
    try {
      const fileArray = Array.from(files || []);

      console.warn('📡 Starting image upload:', {
        fileCount: fileArray.length,
        sessionId: metadata.sessionId,
        maxFilesPerRequest: ImageService.MAX_FILES_PER_UPLOAD_REQUEST
      });

      // Validate sessionId requirement
      if (!metadata.sessionId) {
        throw new Error('sessionId is required for image uploads');
      }

      if (fileArray.length === 0) {
        throw new Error('No files provided for image upload');
      }

      const batches = [];
      for (let start = 0; start < fileArray.length; start += ImageService.MAX_FILES_PER_UPLOAD_REQUEST) {
        const end = start + ImageService.MAX_FILES_PER_UPLOAD_REQUEST;
        batches.push({
          files: fileArray.slice(start, end),
          metadata: {
            sessionId: metadata.sessionId,
            names: metadata.names?.slice(start, end) || [],
            descriptions: metadata.descriptions?.slice(start, end) || [],
            tags: metadata.tags?.slice(start, end) || []
          }
        });
      }

      if (batches.length > 1) {
        console.warn('⚠️ Upload exceeds backend per-request file limit, sending in batches', {
          totalFiles: fileArray.length,
          batchCount: batches.length,
          batchSize: ImageService.MAX_FILES_PER_UPLOAD_REQUEST
        });
      }

      let uploaded = 0;
      let failed = 0;
      let total = 0;
      const uploadedImages = [];
      const results = [];
      const errors = [];

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
        const currentBatch = batches[batchIndex];
        const formData = this.buildUploadFormData(currentBatch.files, currentBatch.metadata);

        console.warn('📦 Uploading image batch', {
          batchNumber: batchIndex + 1,
          batchCount: batches.length,
          filesInBatch: currentBatch.files.length,
          fileNames: currentBatch.files.map(file => file.name)
        });

        const config = {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000,
        };

        if (onProgress) {
          config.onUploadProgress = (progressEvent) => {
            const batchLoaded = progressEvent.loaded || 0;
            const batchTotal = progressEvent.total || 0;
            const filesCompleted = batchIndex * ImageService.MAX_FILES_PER_UPLOAD_REQUEST;
            const estimatedLoaded = filesCompleted + (batchTotal > 0
              ? (batchLoaded / batchTotal) * currentBatch.files.length
              : 0);
            const percentCompleted = Math.min(
              100,
              Math.round((estimatedLoaded / fileArray.length) * 100)
            );
            onProgress(percentCompleted);
          };
        }

        let data;

        try {
          const response = await apiClient.post('/upload', formData, config);
          data = response.data || {};
        } catch (error) {
          const statusCode = error.response?.status;
          const responseData = error.response?.data;

          if (statusCode === 400 && responseData && Array.isArray(responseData.errors)) {
            console.warn('⚠️ Image batch upload returned only per-file errors', {
              batchNumber: batchIndex + 1,
              batchCount: batches.length,
              errorCount: responseData.errors.length,
              errors: responseData.errors
            });
            data = responseData;
          } else {
            throw error;
          }
        }

        uploaded += data.uploaded || 0;
        failed += data.failed || 0;
        total += data.total || currentBatch.files.length;

        if (Array.isArray(data.uploadedImages)) {
          uploadedImages.push(...data.uploadedImages);
        }
        if (Array.isArray(data.results)) {
          results.push(...data.results);
        }
        if (Array.isArray(data.errors)) {
          errors.push(...data.errors);
        }

        console.warn('✅ Image batch upload completed', {
          batchNumber: batchIndex + 1,
          batchCount: batches.length,
          uploaded: data.uploaded,
          failed: data.failed,
          total: data.total
        });
      }

      const aggregatedResult = {
        uploaded,
        failed,
        total,
        uploadedImages,
        results,
        errors
      };

      console.warn('✅ Image upload completed:', aggregatedResult);

      if (onProgress) {
        onProgress(100);
      }

      return aggregatedResult;
    } catch (error) {
      console.error('❌ Image upload failed:', error);
      throw this.handleError(error, 'Failed to upload images');
    }
  }

  /**
   * Update image metadata
   * @param {string} imageId - Image unique ID
   * @param {Object} updates - Metadata updates
   * @returns {Promise} Updated image data
   */
  async updateImage(imageId, updates) {
    try {
      console.log('📡 Updating image:', imageId, updates);
      
      const response = await apiClient.put(`/${imageId}`, updates);
      
      console.log('✅ Image updated successfully:', {
        id: response.data.uniqueId,
        name: response.data.name
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to update image:', error);
      throw this.handleError(error, 'Failed to update image');
    }
  }

  /**
   * Delete image
   * @param {string} imageId - Image unique ID
   * @returns {Promise} Deletion result
   */
  async deleteImage(imageId) {
    try {
      console.log('📡 Deleting image:', imageId);
      
      const response = await apiClient.delete(`/${imageId}`);
      
      console.log('✅ Image deleted successfully:', imageId);
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to delete image:', error);
      throw this.handleError(error, 'Failed to delete image');
    }
  }

  /**
   * Delete multiple images
   * @param {string[]} imageIds - Array of image unique IDs
   * @returns {Promise} Delete operation results
   */
  async deleteImages(imageIds) {
    try {
      console.log('📡 Deleting multiple images:', imageIds.length, 'images');
      
      const response = await apiClient.delete('/bulk', {
        data: { imageIds }
      });
      
      console.log('✅ Images deleted successfully:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to delete images:', error);
      throw this.handleError(error, 'Failed to delete images');
    }
  }

  /**
   * Get download URL for image
   * @param {string} imageId - Image unique ID
   * @returns {Promise} Download URL and metadata
   */
  async getDownloadUrl(imageId) {
    try {
      console.log('📡 Getting download URL for image:', imageId);
      
      const response = await apiClient.get(`/${imageId}/download`);
      
      console.log('✅ Download URL generated successfully:', imageId);
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get download URL:', error);
      throw this.handleError(error, 'Failed to get download URL');
    }
  }

  /**
   * Download image file
   * @param {string} imageId - Image unique ID
   * @param {string} filename - Filename for download
   * @returns {Promise} Download initiated
   */
  async downloadImage(imageId, filename) {
    try {
      const downloadData = await this.getDownloadUrl(imageId);
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadData.downloadUrl;
      link.download = filename || downloadData.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ Image download initiated:', filename);
    } catch (error) {
      console.error('❌ Failed to download image:', error);
      throw this.handleError(error, 'Failed to download image');
    }
  }

  /**
   * Get image statistics
   * @returns {Promise} Image statistics
   */
  async getStats() {
    try {
      console.log('📡 Fetching image statistics');
      
      const response = await apiClient.get('/stats');
      
      console.log('✅ Image statistics fetched:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch image statistics:', error);
      throw this.handleError(error, 'Failed to fetch statistics');
    }
  }

  /**
   * Search images by query
   * @param {string} query - Search query
   * @param {Object} filters - Additional filters
   * @returns {Promise} Search results
   */
  async searchImages(query, filters = {}) {
    try {
      const params = {
        search: query,
        ...filters
      };
      
      return await this.getImages(params);
    } catch (error) {
      console.error('❌ Failed to search images:', error);
      throw this.handleError(error, 'Failed to search images');
    }
  }

  /**
   * Get images with GPS data
   * @param {Object} params - Query parameters
   * @returns {Promise} Images with GPS coordinates
   */
  async getImagesWithGPS(params = {}) {
    try {
      const searchParams = {
        ...params,
        hasGPS: 'true'
      };
      
      return await this.getImages(searchParams);
    } catch (error) {
      console.error('❌ Failed to fetch images with GPS:', error);
      throw this.handleError(error, 'Failed to fetch images with GPS data');
    }
  }

  /**
   * Filter images by date range
   * @param {string} dateFrom - Start date (ISO string)
   * @param {string} dateTo - End date (ISO string)
   * @param {Object} additionalParams - Additional query parameters
   * @returns {Promise} Filtered images
   */
  async getImagesByDateRange(dateFrom, dateTo, additionalParams = {}) {
    try {
      const params = {
        dateFrom,
        dateTo,
        ...additionalParams
      };
      
      return await this.getImages(params);
    } catch (error) {
      console.error('❌ Failed to fetch images by date range:', error);
      throw this.handleError(error, 'Failed to fetch images by date range');
    }
  }

  /**
   * Get images by tags
   * @param {Array|string} tags - Tags to filter by
   * @param {Object} additionalParams - Additional query parameters
   * @returns {Promise} Tagged images
   */
  async getImagesByTags(tags, additionalParams = {}) {
    try {
      const tagString = Array.isArray(tags) ? tags.join(',') : tags;
      const params = {
        tags: tagString,
        ...additionalParams
      };
      
      return await this.getImages(params);
    } catch (error) {
      console.error('❌ Failed to fetch images by tags:', error);
      throw this.handleError(error, 'Failed to fetch images by tags');
    }
  }

  /**
   * Analyze selected images using Vision APIs (OpenAI or Azure)
   * @param {Array<string>} imageIds - Array of image IDs to analyze
   * @param {string} prompt - Optional custom prompt for analysis
   * @param {string} provider - Vision API provider ('openai' or 'azure')
   * @returns {Promise<Object>} Analysis results
   */
  async analyzeImages(imageIds, prompt = null, provider = 'openai') {
    try {
      console.log('🧠 Analyzing images:', { imageIds, prompt, provider });
      
      const response = await apiClient.post('/analyze', {
        imageIds,
        prompt,
        provider
      });
      
      console.log(`✅ Image analysis completed with ${provider}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Image analysis with ${provider} failed:`, error);
      throw this.handleError(error, 'Failed to analyze images');
    }
  }

  /**
   * Recognize and group faces in selected images using Azure Face API
   * @param {Array<string>} imageIds - Array of image IDs to analyze faces in
   * @param {boolean} includeAttributes - Whether to include face attributes in the result
   * @returns {Promise<Object>} Recognition results with face groups
   */
  async recognizeFaces(imageIds, includeAttributes = false) {
    try {
      const response = await apiClient.post('/recognize-faces', {
        imageIds,
        includeAttributes
      });
      return response.data;
    } catch (error) {
      console.error('❌ Failed to recognize faces:', error);
      throw this.handleError(error, 'Failed to recognize faces in images');
    }
  }

  /**
   * Find person matches using existing reference persons in AWS collection
   * @param {string} sessionId - Session ID to search in
   * @param {Function} progressCallback - Progress callback function
   * @param {Array} selectedImageIds - Array of image IDs to search (optional, searches all if not provided)
   * @param {Object} options - Search options (mainFacesOnly, showAllFaces)
   * @returns {Promise<Object>} Matching results with similarity scores organized by reference person
   */
  async findPersonMatchesWithAws(sessionId, progressCallback = null, selectedImageIds = null, options = {}) {
    try {
      console.log('📡 Finding person matches using AWS Rekognition reference persons in session:', sessionId);
      if (selectedImageIds && selectedImageIds.length > 0) {
        console.log('🎯 Searching only selected images:', selectedImageIds.length, 'images');
      } else {
        console.log('🔍 Searching all images in session');
      }
      console.log('⚙️ Search options:', options);
      
      // Join the Socket.IO room BEFORE firing the POST so no progress events are missed.
      // (Backend uses setImmediate, but the socket must be in the room first.)
      if (progressCallback) {
        const socket = socketIOClient(API_BASE_URL, { path: '/socket.io/', withCredentials: true, transports: ['polling'] });

        // Wait until server ACKs the room join before firing the POST.
        // With HTTP polling, emit is queued — without the ack the POST can arrive
        // at the backend before the join is processed, and all events are missed.
        await new Promise((resolve) => {
          const timeout = setTimeout(resolve, 3000); // fallback: don't block forever
          socket.on('connect', () => {
            console.log('✅ [FaceRec] Socket.IO connected, joining room:', sessionId);
            socket.emit('joinFaceRecognitionRoom', { sessionId }, (ack) => {
              console.log('✅ [FaceRec] Room join acknowledged by server:', ack);
              clearTimeout(timeout);
              resolve();
            });
          });
          socket.on('connect_error', (err) => {
            console.error('❌ [FaceRec] Socket.IO connect error:', err.message);
            clearTimeout(timeout);
            resolve(); // don't block the POST if socket fails
          });
        });

        socket.on('faceRecognitionProgress', (data) => {
          if (data.sessionId !== sessionId) return;
          progressCallback({ ...data, isCompleted: false, hasError: false });
        });
        socket.on('faceRecognitionComplete', (data) => {
          if (data.sessionId !== sessionId) return;
          progressCallback({ ...data });
          setTimeout(() => socket.disconnect(), 1000);
        });
      }
      
      // Use the correct face-recognition API endpoint, not images API
      const faceRecognitionClient = axios.create({
        baseURL: `${API_BASE_URL}/api/face-recognition`,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 300000, // 5 minutes for face recognition (increased from 2 minutes)
      });

      // Add auth token
      const token = localStorage.getItem('token');
      if (token) {
        faceRecognitionClient.defaults.headers.Authorization = `Bearer ${token}`;
      }
      
      // Prepare request body
      const requestBody = { ...options }; // Include search options
      if (selectedImageIds && selectedImageIds.length > 0) {
        requestBody.selectedImageIds = selectedImageIds;
      }
      
      const response = await faceRecognitionClient.post(`/find-with-reference-persons/${sessionId}`, requestBody);
      
      console.log('✅ AWS Rekognition found matches:', { 
        count: response.data.matches?.length || 0,
        referencePersons: response.data.referencePersons?.length || 0
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to find matches with AWS Rekognition:', error);
      throw this.handleError(error, 'Failed to find person matches with AWS Rekognition');
    }
  }

  /**
   * Cancel AWS Face Recognition operation for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Cancellation result
   */
  async cancelAwsFaceRecognition(sessionId) {
    try {
      console.log('🛑 Cancelling AWS Face Recognition for session:', sessionId);
      
      const faceRecognitionClient = axios.create({
        baseURL: `${API_BASE_URL}/api/face-recognition`,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000,
      });

      // Add auth token
      const token = localStorage.getItem('token');
      if (token) {
        faceRecognitionClient.defaults.headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await faceRecognitionClient.post(`/cancel-aws/${sessionId}`);
      
      console.log('✅ Cancellation requested successfully');
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to cancel AWS Face Recognition:', error);
      throw this.handleError(error, 'Failed to cancel AWS Face Recognition');
    }
  }

  /**
   * Force-reset a stuck inProgress flag for a session (when the process crashed/died)
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Reset result
   */
  async forceResetAwsFaceRecognition(sessionId) {
    try {
      console.log('🔄 Force-resetting AWS Face Recognition state for session:', sessionId);

      const faceRecognitionClient = axios.create({
        baseURL: `${API_BASE_URL}/api/face-recognition`,
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
      });

      const token = localStorage.getItem('token');
      if (token) {
        faceRecognitionClient.defaults.headers.Authorization = `Bearer ${token}`;
      }

      const response = await faceRecognitionClient.post(`/force-reset-aws/${sessionId}`);
      console.log('✅ Force-reset succeeded');
      return response.data;
    } catch (error) {
      console.error('❌ Failed to force-reset AWS Face Recognition:', error);
      throw this.handleError(error, 'Failed to force-reset AWS Face Recognition');
    }
  }

  /**
   * Force-reset stuck embedding generation inProgress flag for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Reset result
   */
  async forceResetEmbeddings(sessionId) {
    try {
      console.log('🔄 Force-resetting embedding generation state for session:', sessionId);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/embeddings/force-reset/${sessionId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Force-reset failed');
      console.log('✅ Embedding force-reset succeeded');
      return await response.json();
    } catch (error) {
      console.error('❌ Failed to force-reset embedding generation:', error);
      throw error;
    }
  }

  /**
   * Get AWS Face Recognition operation status for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Operation status
   */
  async getAwsFaceRecognitionStatus(sessionId) {
    try {
      const faceRecognitionClient = axios.create({
        baseURL: `${API_BASE_URL}/api/face-recognition`,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000,
      });

      // Add auth token
      const token = localStorage.getItem('token');
      if (token) {
        faceRecognitionClient.defaults.headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await faceRecognitionClient.get(`/aws-status/${sessionId}`);
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get AWS Face Recognition status:', error);
      throw this.handleError(error, 'Failed to get AWS Face Recognition status');
    }
  }

  /**
   * Get Vision services status (OpenAI and Azure)
   * @returns {Promise<Object>} Services status
   */
  async getAnalysisServiceStatus() {
    try {
      const response = await apiClient.get('/analyze/status');
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get analysis service status:', error);
      throw this.handleError(error, 'Failed to get analysis service status');
    }
  }

  /**
   * Handle API errors and provide user-friendly messages
   * @param {Error} error - API error
   * @param {string} defaultMessage - Default error message
   * @returns {Error} Formatted error
   */
  handleError(error, defaultMessage = 'An error occurred') {
    let message = defaultMessage;
    let details = null;

    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          message = data.message || 'Invalid request';
          break;
        case 401:
          message = 'Authentication required';
          break;
        case 403:
          message = 'Access denied';
          break;
        case 404:
          message = 'Image not found';
          break;
        case 413:
          message = 'File too large';
          break;
        case 429:
          message = 'Too many requests. Please try again later.';
          break;
        case 500:
          message = 'Server error. Please try again.';
          break;
        default:
          message = data.message || `Error ${status}`;
      }
      
      details = data.details || data.error;
    } else if (error.request) {
      // Network error
      message = 'Network error. Please check your connection.';
    } else {
      // Other error
      message = error.message || defaultMessage;
    }

    const formattedError = new Error(message);
    formattedError.originalError = error;
    formattedError.details = details;
    formattedError.status = error.response?.status;

    return formattedError;
  }

  /**
   * Validate image files before upload
   * @param {FileList|Array} files - Files to validate
   * @returns {Object} Validation result
   */
  validateFiles(files) {
    const errors = [];
    const validFiles = [];
    const maxSize = 50 * 1024 * 1024; // 50MB
    const supportedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png', 
      'image/gif', 
      'image/webp', 
      'image/bmp', 
      'image/tiff'
    ];

    Array.from(files).forEach((file, index) => {
      const fileErrors = [];

      // Check file type
      if (!supportedTypes.includes(file.type)) {
        fileErrors.push(`Unsupported file type: ${file.type}`);
      }

      // Check file size
      if (file.size > maxSize) {
        fileErrors.push(`File too large: ${this.formatFileSize(file.size)}. Maximum size: ${this.formatFileSize(maxSize)}`);
      }

      // Check filename
      if (!file.name || file.name.trim() === '') {
        fileErrors.push('Invalid filename');
      }

      if (fileErrors.length > 0) {
        errors.push({
          file: file.name,
          index,
          errors: fileErrors
        });
      } else {
        validFiles.push(file);
      }
    });

    return {
      valid: errors.length === 0,
      validFiles,
      errors,
      validCount: validFiles.length,
      errorCount: errors.length,
      totalCount: files.length
    };
  }

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // ========================
  // SCENE TAGGING METHODS
  // ========================

  /**
   * Analyze selected photos in a session for scene classification with real-time updates
   * @param {string} sessionId - Session ID to analyze
   * @param {Array} selectedImageIds - Array of image IDs to analyze (optional, analyzes all if empty)
   * @param {Object} options - Analysis options
   * @returns {Promise} Scene analysis results
   */
  async analyzeSessionScenes(sessionId, selectedImageIds = [], options = {}) {
    try {
      console.log('📡🚀📡 IMAGE SERVICE: Starting session scene analysis:', sessionId);
      console.log('📡 Selected images:', selectedImageIds.length > 0 ? selectedImageIds : 'all images');
      console.log('📡 API URL will be:', `${API_BASE_URL}/api/scenes/analyze-session`);
      console.log('📡 Request payload:', { sessionId, selectedImageIds, options });
      console.log('📡 Auth token exists:', !!localStorage.getItem('token'));
      console.log('📡 Auth token preview:', localStorage.getItem('token')?.substring(0, 50) + '...');
      
      const requestConfig = {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000 // 10 seconds for immediate response, analysis continues via WebSocket
      };
      
      console.log('📡 Request config:', requestConfig);
      console.log('📡 About to make axios.post request...');
      
      const response = await axios.post(`${API_BASE_URL}/api/scenes/analyze-session`, {
        sessionId,
        selectedImageIds,
        options
      }, requestConfig);
      
      console.log('✅ Session scene analysis started:', {
        analysisJobId: response.data.analysisJobId,
        status: response.data.status,
        sessionId: response.data.sessionId,
        totalImages: response.data.totalImages,
        selectedImages: response.data.selectedImages,
        fullResponse: response.data
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Session scene analysis failed:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      
      if (error.response?.status === 401) {
        console.error('🔒 Authentication failed - token may be expired');
        localStorage.removeItem('token'); // Clear invalid token
        throw new Error('Authentication failed. Please login again.');
      }
      
      throw this.handleError(error, 'Failed to analyze session scenes');
    }
  }

  /**
   * Cancel an in-progress scene analysis job
   * @param {string} jobId - Analysis job ID to cancel
   */
  async cancelSceneAnalysis(jobId) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/scenes/cancel-analysis/${jobId}`,
        {},
        {
          withCredentials: true,
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        }
      );
      return response.data;
    } catch (error) {
      console.error('❌ Failed to cancel scene analysis:', error);
      throw this.handleError(error, 'Failed to cancel scene analysis');
    }
  }

  /**
   * Get existing scene analysis results for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise} Scene data
   */
  async getSessionScenes(sessionId) {
    try {
      console.log('📡 Fetching session scene data:', sessionId);
      console.log('📡 API URL will be:', `${API_BASE_URL}/api/scenes/session/${sessionId}`);
      console.log('📡 Auth token exists:', !!localStorage.getItem('token'));
      
      const response = await axios.get(`${API_BASE_URL}/api/scenes/session/${sessionId}?_t=${Date.now()}`, {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('✅ Session scene data fetched:', {
        sessionId,
        totalImages: response.data.totalImages,
        sceneGroups: response.data.sceneGroups?.length || 0,
        fullResponse: response.data
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch session scene data:', error);
      throw this.handleError(error, 'Failed to fetch scene data');
    }
  }

  /**
   * Manually assign or update scene groups for images
   * @param {Array} assignments - Array of {imageId, groupId, groupName} objects
   * @returns {Promise} Assignment results
   */
  async assignSceneGroups(assignments) {
    try {
      console.log('📡 Assigning scene groups:', assignments.length);
      
      const response = await axios.post(`${API_BASE_URL}/api/scenes/assign-groups`, {
        assignments
      }, {
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Scene groups assigned:', {
        updated: response.data.updated,
        total: response.data.total
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to assign scene groups:', error);
      throw this.handleError(error, 'Failed to assign scene groups');
    }
  }

  /**
   * Analyze individual image for scene classification
   * @param {string} imageId - Image ID to analyze
   * @param {Object} options - Analysis options
   * @returns {Promise} Scene analysis result
   */
  async analyzeImageScene(imageId, options = {}) {
    try {
      console.log('📡 Analyzing image scene:', imageId);
      
      const response = await this.analyzeImages([imageId], options.customPrompt || null, 'openai');
      
      if (response.success && response.results.length > 0) {
        console.log('✅ Image scene analyzed:', {
          imageId,
          success: response.results[0].success
        });
        
        return {
          success: true,
          sceneData: this.parseSceneFromAnalysis(response.results[0].analysis),
          rawAnalysis: response.results[0].analysis
        };
      } else {
        throw new Error('Scene analysis failed');
      }
    } catch (error) {
      console.error('❌ Image scene analysis failed:', error);
      throw this.handleError(error, 'Failed to analyze image scene');
    }
  }

  /**
   * Parse scene information from general AI analysis
   * @param {string} analysis - AI analysis text
   * @returns {Object} Parsed scene data
   */
  parseSceneFromAnalysis(analysis) {
    // Simple parsing logic for extracting scene info from general analysis
    const lowerAnalysis = analysis.toLowerCase();
    
    // Detect scene type based on keywords
    let sceneType = 'Unknown';
    if (lowerAnalysis.includes('indoor') || lowerAnalysis.includes('inside') || lowerAnalysis.includes('room')) {
      sceneType = 'Indoor Social';
    } else if (lowerAnalysis.includes('outdoor') || lowerAnalysis.includes('outside') || lowerAnalysis.includes('nature')) {
      sceneType = 'Outdoor Nature';
    } else if (lowerAnalysis.includes('event') || lowerAnalysis.includes('party') || lowerAnalysis.includes('celebration')) {
      sceneType = 'Events & Celebrations';
    } else if (lowerAnalysis.includes('food') || lowerAnalysis.includes('dining') || lowerAnalysis.includes('restaurant')) {
      sceneType = 'Food & Dining';
    } else if (lowerAnalysis.includes('sport') || lowerAnalysis.includes('activity') || lowerAnalysis.includes('exercise')) {
      sceneType = 'Sports & Activities';
    }
    
    // Detect time context
    let timeContext = 'Unknown';
    if (lowerAnalysis.includes('morning') || lowerAnalysis.includes('dawn')) {
      timeContext = 'Morning';
    } else if (lowerAnalysis.includes('afternoon') || lowerAnalysis.includes('noon')) {
      timeContext = 'Afternoon';
    } else if (lowerAnalysis.includes('evening') || lowerAnalysis.includes('sunset')) {
      timeContext = 'Evening';
    } else if (lowerAnalysis.includes('night') || lowerAnalysis.includes('dark')) {
      timeContext = 'Night';
    }
    
    // Detect location type
    let locationType = 'Unknown';
    if (lowerAnalysis.includes('indoor') || lowerAnalysis.includes('inside')) {
      locationType = 'Indoor';
    } else if (lowerAnalysis.includes('outdoor') || lowerAnalysis.includes('outside')) {
      locationType = 'Outdoor';
    }
    
    // Extract potential tags (simple keyword extraction)
    const tags = [];
    const keywords = analysis.match(/\b\w+\b/g) || [];
    const relevantKeywords = keywords.filter(word => 
      word.length > 3 && 
      !['the', 'and', 'this', 'that', 'with', 'have', 'from', 'they', 'were', 'been', 'their'].includes(word.toLowerCase())
    );
    tags.push(...relevantKeywords.slice(0, 5));
    
    return {
      sceneType,
      sceneDetails: analysis.substring(0, 200) + (analysis.length > 200 ? '...' : ''),
      tags: [...new Set(tags)], // Remove duplicates
      timeContext,
      locationType,
      socialContext: 'Unknown'
    };
  }

  /**
   * Get imageIds of embeddings that have no cluster assignment
   * GET /api/embeddings/unclustered/session/:sessionId
   */
  async getUnclusteredImages(sessionId) {
    const response = await axios.get(`${API_BASE_URL}/api/embeddings/unclustered/session/${sessionId}`, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    return response.data;
  }

  // ─── Clustering ───────────────────────────────────────────────────────────

  /**
   * Get embedding + cluster stats for a session
   * GET /api/embeddings/session/:sessionId/stats
   */
  async getEmbeddingStats(sessionId) {
    const response = await axios.get(`${API_BASE_URL}/api/embeddings/session/${sessionId}/stats`, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    return response.data;
  }

  /**
   * Get existing clusters for a session
   * GET /api/embeddings/clusters/session/:sessionId
   */
  async getSessionClusters(sessionId) {
    const response = await axios.get(`${API_BASE_URL}/api/embeddings/clusters/session/${sessionId}`, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    return response.data;
  }

  /**
   * Run clustering on a session (synchronous)
   * POST /api/embeddings/cluster/session/:sessionId
   * @param {string} sessionId
   * @param {Object} options – { similarityThreshold, minClusterSize, maxClusters, useMetadata, filters }
   */
  async clusterSessionImages(sessionId, options = {}) {
    const response = await axios.post(
      `${API_BASE_URL}/api/embeddings/cluster/session/${sessionId}`,
      options,
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        timeout: 300000, // 5 min — clustering large sessions can be slow
      }
    );
    return response.data;
  }
}

// Export singleton instance
const imageServiceInstance = new ImageService();
export default imageServiceInstance;

